import {
  AppWindow,
  ArrowDownToLine,
  ArrowUpFromLine,
  Award,
  Palette,
  ShieldAlert,
  Shirt,
  BellRing,
  BookOpen,
  Bug,
  Briefcase,
  Building2,
  Cake,
  Calculator,
  CalendarClock,
  CalendarDays,
  CalendarOff,
  ChartColumn,
  ChartLine,
  CircleHelp,
  CirclePlus,
  ClipboardCheck,
  ClipboardList,
  Coins,
  FileCheck,
  FileCog,
  FileCheck2,
  FileSignature,
  FileUp,
  Gauge,
  GitBranch,
  Handshake,
  HardHat,
  HeartHandshake,
  History,
  House,
  IdCard,
  Inbox,
  Headset,
  ListChecks,
  MessageSquarePlus,
  KeyRound,
  Landmark,
  Laptop,
  LayoutGrid,
  Link2,
  Megaphone,
  Library,
  LifeBuoy,
  ListTree,
  MapPin,
  MonitorCog,
  Gift,
  MessageSquareWarning,
  Network,
  Package,
  PackageCheck,
  PackageSearch,
  PiggyBank,
  Plane,
  Receipt,
  ReceiptText,
  Scale,
  ScrollText,
  ScanLine,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Sparkles,
  Store,
  Tags,
  TrendingUp,
  TriangleAlert,
  type LucideIcon,
  Upload,
  UserCheck,
  UserPlus,
  UserRound,
  Server,
  CalendarCheck,
  Siren,
  Boxes,
  GitPullRequestArrow,
  FolderKanban,
  Users,
  Variable,
  Vault,
  Wind,
  Wrench,
  CalendarRange,
  Timer,
} from 'lucide-react';

/**
 * Uma tela do menu (uma funcionalidade): endereço, chave do texto, ícone e a permissão
 * para ver (null = todos). As operações de cada tela (criar, aprovar…) são as permissões
 * `<módulo>.<entidade>.<ação>` conferidas dentro dela e no servidor.
 */
/**
 * Quem foi nomeado aprovador (gestor do centro, responsável, CEO ou substituto) decide sem ter
 * perfil (ADR 0080). Não é uma permissão: vem de `/auth/me` e abre só as telas de quem aprova.
 */
export const APPROVER_NOMINEE = 'approval.nominee';

export interface NavItem {
  to: string;
  key: string;
  icon: LucideIcon;
  /** Basta ter uma delas (null = todos). */
  permission: string | readonly string[] | null;
  /** Página inicial do departamento: só fica marcada no menu quando é a tela aberta. */
  overview?: boolean;
}

/** Um módulo do departamento: um grupo de telas. Sem título = telas do dia a dia, no topo. */
export interface NavGroup {
  key: string | null;
  items: NavItem[];
  /** Seção do departamento a que o grupo pertence (só nos departamentos com `sections`). */
  section?: string;
}

/**
 * Seção de um departamento de menu enxuto: uma entrada só no menu, que abre uma página com as
 * telas dos grupos dela (`/<departamento>/<seção>`). O texto de apresentação fica em
 * `departmentHome.sectionAbout.<id>`.
 */
export interface NavSection {
  id: string;
  key: string;
  icon: LucideIcon;
}

/** Um departamento: uma entrada na lateral, com os seus módulos e telas. */
export interface NavArea {
  id: string;
  /** Nome completo: lateral, caminho de navegação e busca. */
  key: string;
  /** Nome curto, guardado da antiga barra de departamentos (ADR 0012); a lateral usa o completo. */
  railKey?: string;
  icon: LucideIcon;
  /** Fica depois da divisória, abaixo dos departamentos (Ajuda e Dados Mestres). */
  bottom?: boolean;
  /** Menu enxuto: o menu mostra a visão geral e uma entrada por seção, não cada tela. */
  sections?: NavSection[];
  groups: NavGroup[];
}

/** A página inicial de um departamento; o texto de apresentação fica em `departmentHome.about.<id>`. */
function overview(to: string, key = 'nav.overview'): NavItem {
  return { to, key, icon: LayoutGrid, permission: null, overview: true };
}

/** Departamento ainda sem módulos: só a página inicial, que avisa que está em preparação. */
function preparing(id: string, icon: LucideIcon, railKey?: string): NavArea {
  return {
    id,
    key: `areas.${id}`,
    railKey,
    icon,
    groups: [{ key: null, items: [overview(`/${id}`)] }],
  };
}

/**
 * Lateral única (docs/guia-visual/README.md, "Navegação"; ADR 0012 e ADR 0071).
 * Hierarquia: departamento → módulo (grupo) → funcionalidade (tela) → operação (permissão).
 * Ordem: Início e os departamentos no topo; Ajuda e Dados Mestres depois da divisória.
 */
/** Quem vê a Central de Conhecimento: leitura ou qualquer papel no fluxo de revisão. */
const KNOWLEDGE_READ = [
  'knowledge.document.read',
  'knowledge.document.drafts',
  'knowledge.document.review',
  'knowledge.document.approve',
  'knowledge.document.publish',
  'knowledge.library.manage',
];

/** Quem usa o patrimônio: consulta, opera (Facilities ou TI), Controladoria ou inventário. */
const ASSET_READ = [
  'asset.asset.read',
  'asset.facilities.manage',
  'asset.it.manage',
  'asset.accounting.manage',
  'asset.inventory.manage',
];
/** Quem vê as contratações de TI: leitura, quem atribui licenças ou quem administra. */
const IT_SUBSCRIPTION_READ = [
  'it.subscription.read',
  'it.license.assign',
  'it.subscription.manage',
];
/** Quem consulta colaboradores, organograma, movimentações e rateios (ADR 0039). */
const PEOPLE_READ = [
  'people.org.read',
  'people.person.manage',
  'people.employee.manage',
  'people.allocation.manage',
];
/** Cadastro administrativo de colaboradores (movimentações e rateios): sem a consulta organizacional. */
const PEOPLE_ADMIN = ['people.person.manage', 'people.employee.manage', 'people.allocation.manage'];
/** Pessoas: cadastro administrativo, também de quem cuida só dos dados pessoais restritos. */
const PERSON_READ = [...PEOPLE_ADMIN, 'people.private.read', 'people.private.manage'];
/** Quem vê a lista de fornecedores. */
const PARTNER_READ = [
  'partner.partner.read',
  'partner.partner.manage',
  'partner.partner.approve',
  'partner.bank.read',
  'partner.bank.manage',
  'partner.bank.approve',
  'partner.payment_terms.manage',
  'partner.document.review_commercial',
  'partner.document.review_financial',
  'partner.document.review_legal',
  'partner.portal.read',
  'partner.portal.invite',
  'partner.portal.revoke',
];
/** Quem abre alguma das listas dos cartões da Visão geral de Dados Mestres (ADR 0097). */
const MASTERDATA_PENDING = [
  'core.cost_center.read',
  'core.cost_center.manage',
  'structure.catalog.read',
  ...PERSON_READ,
  ...PARTNER_READ,
];
/** Catálogo de cargos: consulta, administração ou quem cuida dos valores salariais. */
const CAREER_READ = [
  'people.org.read',
  'career.catalog.manage',
  'career.salary.read',
  'career.salary.manage',
];
/** Quem vê a parte técnica dos equipamentos de TI (Fase 2): consulta ou administração técnica. */
const IT_DEVICE_READ = ['it.device.read', 'it.device.manage'];
const IT_ACCESS_READ = ['it.access.read', 'it.access.manage'];
const IT_GOVERNANCE_READ = ['it.governance.read', 'it.governance.manage'];
const IT_PROJECT_READ = ['it.project.read', 'it.project.manage'];
const IT_FINANCE_READ = [
  'it.finance.read',
  'it.service.manage',
  'it.invoice.manage',
  'it.invoice.confirm_payment',
];
/**
 * FP&A (ADR 0032). Os números ficam com as permissões do FP&A. Os cadastros corporativos são
 * mantidos e consultados em Dados Mestres (ADR 0049, decisão M1).
 */
const FPA_PLAN = ['fpa.data.read', 'fpa.plan.edit', 'fpa.plan.manage', 'fpa.plan.approve'];
/** Administração do FP&A no menu: quem configura o planejamento ou conduz as versões. */
const FPA_ADMIN = ['fpa.settings.manage', 'fpa.plan.manage'];
const FPA_ADMIN_CYCLE = [...FPA_ADMIN, 'fpa.period.close'];

export const AREAS: NavArea[] = [
  {
    id: 'home',
    key: 'areas.home',
    icon: House,
    groups: [
      {
        key: null,
        items: [
          { to: '/', key: 'nav.home', icon: House, permission: null },
          // Bens sob a guarda da pessoa e os termos dela (ADR 0031): para todos.
          { to: '/assets/mine', key: 'nav.assetsMine', icon: UserCheck, permission: null },
          // Contratos de que a pessoa é gestora ou que pediu (auditoria de 27/10, Onda 2): para todos.
          {
            to: '/legal/my-contracts',
            key: 'nav.myContracts',
            icon: FileSignature,
            permission: null,
          },
          // Meu cadastro (ADR 0083): foto, tamanhos, restrições e aniversário, para todos.
          { to: '/me', key: 'nav.myProfile', icon: UserRound, permission: null },
          // Requisições de vaga (ADR 0091): pede o gestor do centro de custo ou o RH/DP; a lista
          // mostra só o que a pessoa pode ver.
          { to: '/people/positions', key: 'nav.positions', icon: UserPlus, permission: null },
        ],
      },
    ],
  },
  // Reúne os cartões das frentes (ADR 0097): só a página inicial, com permissão própria.
  {
    id: 'executive',
    key: 'areas.executive',
    railKey: 'areasShort.executive',
    icon: Gauge,
    groups: [
      {
        key: null,
        items: [{ ...overview('/executive'), permission: 'executive.dashboard.view' }],
      },
    ],
  },
  {
    id: 'finance',
    key: 'areas.finance',
    icon: Landmark,
    groups: [
      {
        key: null,
        items: [
          overview('/finance'),
          {
            to: '/finance/pending',
            key: 'nav.financePending',
            icon: ListChecks,
            permission: ['finance.payable.read', 'finance.treasury.read'],
          },
          {
            to: '/finance/payables',
            key: 'nav.payables',
            icon: ArrowUpFromLine,
            permission: 'finance.payable.read',
          },
          // Ainda sem permissão própria: ela entra junto com cada parte do Financeiro.
          {
            to: '/finance/receivables',
            key: 'nav.receivables',
            icon: ArrowDownToLine,
            permission: null,
          },
          {
            to: '/finance/treasury',
            key: 'nav.treasury',
            icon: Vault,
            permission: 'finance.treasury.read',
          },
          {
            to: '/finance/expense-reimbursements',
            key: 'nav.expenseReimbursements',
            icon: ReceiptText,
            permission: null,
          },
        ],
      },
    ],
  },
  {
    id: 'controlling',
    key: 'areas.controlling',
    railKey: 'areasShort.controlling',
    icon: Calculator,
    groups: [
      {
        key: null,
        items: [
          overview('/controlling'),
          {
            to: '/controlling/actual-queue',
            key: 'nav.controllingQueue',
            icon: ClipboardCheck,
            permission: 'fpa.actual.audit',
          },
          {
            to: '/controlling/actual-sources',
            key: 'nav.controllingSources',
            icon: ListChecks,
            permission: 'fpa.actual.audit',
          },
        ],
      },
    ],
  },
  {
    id: 'purchasing',
    key: 'areas.purchasing',
    icon: ShoppingCart,
    groups: [
      {
        key: null,
        items: [
          overview('/purchasing'),
          {
            to: '/purchasing/tracking',
            key: 'nav.purchasingTracking',
            icon: Gauge,
            permission: ['purchasing.request.create', 'purchasing.request.manage'],
          },
          {
            to: '/purchasing/pending',
            key: 'nav.purchasingPending',
            icon: ListChecks,
            permission: 'purchasing.request.manage',
          },
        ],
      },
      {
        key: 'navGroups.purchasingRequests',
        items: [
          {
            to: '/purchasing/new',
            key: 'nav.purchasingNew',
            icon: CirclePlus,
            permission: 'purchasing.request.create',
          },
          {
            to: '/purchasing/requests',
            key: 'nav.purchasingMine',
            icon: ClipboardList,
            permission: 'purchasing.request.create',
          },
          {
            // Atalho para "Meus prazos" do SLA corporativo (farol das compras sob minha responsabilidade).
            to: '/purchasing/deadlines',
            key: 'nav.purchasingDeadlines',
            icon: Timer,
            permission: ['purchasing.request.manage', 'purchasing.request.create'],
          },
          {
            to: '/approvals',
            key: 'nav.approvals',
            icon: ClipboardCheck,
            permission: ['approval.request.decide', APPROVER_NOMINEE],
          },
        ],
      },
      {
        key: 'navGroups.purchasingOperation',
        items: [
          {
            to: '/purchasing/queue',
            key: 'nav.purchasingQueue',
            icon: Inbox,
            permission: 'purchasing.request.manage',
          },
          {
            to: '/purchasing/deliveries',
            key: 'nav.purchasingDeliveries',
            icon: PackageCheck,
            permission: ['purchasing.receipt.register', 'purchasing.request.manage'],
          },
        ],
      },
      {
        key: 'navGroups.purchasingRules',
        items: [
          {
            to: '/purchasing/quote-rules',
            key: 'nav.purchasingQuoteRules',
            icon: SlidersHorizontal,
            permission: ['purchasing.quote_rule.manage', 'purchasing.request.manage'],
          },
          {
            to: '/partners/documents',
            key: 'nav.partnerDocuments',
            icon: FileCheck,
            permission: [
              'partner.partner.manage',
              'partner.document.review_commercial',
              'partner.document.review_financial',
              'partner.document.review_legal',
            ],
          },
        ],
      },
    ],
  },
  {
    // Facilities (ADR 0027): painel, fila de atendimento, chamados e a gestão (catálogo,
    // formulários e o cadastro corporativo de Locais).
    id: 'facilities',
    key: 'areas.facilities',
    icon: Wrench,
    groups: [
      {
        key: null,
        items: [
          overview('/facilities'),
          {
            to: '/facilities/pending',
            key: 'nav.facilitiesPending',
            icon: ListChecks,
            permission: ['facilities.request.read', 'facilities.work_order.read', ...ASSET_READ],
          },
        ],
      },
      {
        key: 'navGroups.facilitiesRequests',
        items: [
          {
            to: '/facilities/panel',
            key: 'nav.facilitiesPanel',
            icon: Gauge,
            permission: ['facilities.request.create', 'facilities.request.read'],
          },
          {
            to: '/facilities/queue',
            key: 'nav.facilitiesQueue',
            icon: Inbox,
            permission: 'facilities.request.read',
          },
          {
            to: '/facilities/new',
            key: 'nav.facilitiesNew',
            icon: CirclePlus,
            permission: ['facilities.request.create', 'facilities.request.manage'],
          },
          {
            to: '/facilities/requests',
            key: 'nav.facilitiesRequests',
            icon: ClipboardList,
            permission: ['facilities.request.create', 'facilities.request.read'],
          },
        ],
      },
      {
        // Manutenção (ADR 0036): ordens de serviço, preventivas, checklists e desempenho dos prestadores.
        key: 'navGroups.maintenance',
        items: [
          {
            to: '/facilities/work-orders',
            key: 'nav.workOrders',
            icon: Wrench,
            permission: 'facilities.work_order.read',
          },
          // Manutenção preventiva (3b): planos e a agenda das execuções.
          {
            to: '/facilities/maintenance-plans',
            key: 'nav.maintenancePlans',
            icon: CalendarClock,
            permission: 'facilities.work_order.read',
          },
          {
            to: '/facilities/preventive-calendar',
            key: 'nav.preventiveCalendar',
            icon: CalendarDays,
            permission: 'facilities.work_order.read',
          },
          // PMOC (3c-3): climatização por unidade ou prédio, com a validação do responsável técnico.
          {
            to: '/facilities/pmocs',
            key: 'nav.pmoc',
            icon: Wind,
            permission: 'facilities.work_order.read',
          },
          // "Nova OS" é uma ação repetida: fica só na tela Ordens de serviço (o endereço
          // /facilities/work-orders/new continua abrindo a lista com a janela).
          {
            to: '/facilities/maintenance-indicators',
            key: 'nav.maintenanceIndicators',
            icon: ChartColumn,
            permission: 'facilities.work_order.read',
          },
          {
            to: '/facilities/checklists',
            key: 'nav.checklists',
            icon: FileCheck2,
            permission: 'facilities.checklist.manage',
          },
          {
            to: '/facilities/providers',
            key: 'nav.providers',
            icon: HardHat,
            permission: 'facilities.work_order.read',
          },
        ],
      },
      {
        // Materiais de consumo e EPI (Entrega 4): itens, saldos por estoque e entregas de EPI.
        key: 'navGroups.facilitiesMaterials',
        items: [
          {
            to: '/facilities/materials',
            key: 'nav.facilitiesMaterials',
            icon: PackageSearch,
            permission: ['facilities.material.manage', 'facilities.work_order.read'],
          },
          // Serviços administrativos (Entrega 6): garagem e chaves.
          {
            to: '/facilities/services',
            key: 'nav.facilitiesServices',
            icon: KeyRound,
            permission: ['facilities.services.manage', 'facilities.request.read'],
          },
          // Viagens corporativas (Entrega 7): pedido e acompanhamento.
          {
            to: '/facilities/travel',
            key: 'nav.facilitiesTravel',
            icon: Plane,
            permission: 'facilities.services.manage',
          },
          // Relatórios de Facilities (indicadores provisórios, seção 9): equipe e serviços.
          {
            to: '/facilities/reports',
            key: 'nav.facilitiesReports',
            icon: Gauge,
            permission: ['facilities.request.read', 'facilities.services.manage'],
          },
          // Reserva de espaços (F07, regras provisórias): quem abre chamados reserva para si.
          {
            to: '/facilities/spaces',
            key: 'nav.facilitiesSpaces',
            icon: CalendarRange,
            permission: ['facilities.request.create', 'facilities.services.manage'],
          },
        ],
      },
      {
        // Patrimônio compartilhado (ADR 0031): Facilities, TI e Controladoria no mesmo cadastro.
        key: 'navGroups.assets',
        items: [
          {
            to: '/assets/panel',
            key: 'nav.assetsPanel',
            icon: Gauge,
            permission: ASSET_READ,
          },
          { to: '/assets/list', key: 'nav.assetsList', icon: Package, permission: ASSET_READ },
          {
            to: '/assets/terms',
            key: 'nav.assetsTerms',
            icon: FileSignature,
            permission: ASSET_READ,
          },
          {
            to: '/assets/inventories',
            key: 'nav.assetsInventories',
            icon: ScanLine,
            permission: ASSET_READ,
          },
        ],
      },
      {
        key: 'navGroups.assetsManagement',
        items: [
          { to: '/assets/classes', key: 'nav.assetsClasses', icon: Tags, permission: ASSET_READ },
          {
            to: '/assets/policy',
            key: 'nav.assetsPolicy',
            icon: SlidersHorizontal,
            permission: ASSET_READ,
          },
          {
            to: '/assets/import',
            key: 'nav.assetsImport',
            icon: PackageSearch,
            permission: 'asset.import',
          },
        ],
      },
      {
        key: 'navGroups.facilitiesManagement',
        items: [
          {
            to: '/facilities/catalog',
            key: 'nav.facilitiesCatalog',
            icon: FileCog,
            permission: 'facilities.catalog.manage',
          },
          // Parâmetros (3c-1): prazo de reabertura e evidências mínimas das OS.
          {
            to: '/facilities/settings',
            key: 'nav.facilitiesSettings',
            icon: SlidersHorizontal,
            permission: ['facilities.catalog.manage', 'facilities.checklist.manage'],
          },
        ],
      },
    ],
  },
  {
    // Jurídico (ADR 0023 e 0024): painel, fila de atendimento, solicitações e administração do
    // catálogo e formulários.
    id: 'legal',
    key: 'areas.legal',
    icon: Scale,
    groups: [
      { key: null, items: [overview('/legal')] },
      {
        key: 'navGroups.legalRequests',
        items: [
          {
            to: '/legal/panel',
            key: 'nav.legalPanel',
            icon: Gauge,
            permission: ['legal.request.create', 'legal.request.read'],
          },
          {
            to: '/legal/queue',
            key: 'nav.legalQueue',
            icon: Inbox,
            permission: 'legal.request.read',
          },
          {
            to: '/legal/pending',
            key: 'nav.legalPending',
            icon: ListChecks,
            permission: 'legal.request.read',
          },
          {
            to: '/legal/new',
            key: 'nav.legalNew',
            icon: CirclePlus,
            permission: 'legal.request.create',
          },
          {
            to: '/legal/requests',
            key: 'nav.legalRequests',
            icon: ClipboardList,
            permission: ['legal.request.create', 'legal.request.read'],
          },
        ],
      },
      {
        key: 'navGroups.legalContracts',
        items: [
          {
            to: '/legal/contracts',
            key: 'nav.legalContracts',
            icon: FileSignature,
            permission: 'legal.request.read',
          },
          {
            to: '/legal/contracts/import',
            key: 'nav.legalContractsImport',
            icon: FileUp,
            permission: 'legal.request.assign',
          },
          {
            to: '/legal/contracts/reconciliation',
            key: 'nav.legalContractsReconciliation',
            icon: Link2,
            permission: 'legal.request.assign',
          },
        ],
      },
      {
        key: 'navGroups.legalIp',
        items: [
          {
            to: '/legal/ip',
            key: 'nav.legalIp',
            icon: Award,
            permission: 'legal.request.read',
          },
          {
            // Obras e direitos autorais (ADR 0065).
            to: '/legal/ip-works',
            key: 'nav.legalIpWorks',
            icon: Palette,
            permission: 'legal.request.read',
          },
          {
            // Licenciamentos e royalties (ADR 0065, E2).
            to: '/legal/ip-licenses',
            key: 'nav.legalIpLicenses',
            icon: Handshake,
            permission: 'legal.request.read',
          },
          {
            // Ocorrências de pirataria (ADR 0065, E3).
            to: '/legal/piracy',
            key: 'nav.legalPiracy',
            icon: ShieldAlert,
            permission: 'legal.request.read',
          },
          {
            to: '/legal/ip/import',
            key: 'nav.legalIpImport',
            icon: FileUp,
            permission: 'legal.request.assign',
          },
          {
            to: '/legal/ip-works/import',
            key: 'nav.legalIpWorkImport',
            icon: FileUp,
            permission: 'legal.request.assign',
          },
        ],
      },
      {
        // Biblioteca Jurídica (ADR 0038 e 0060): administrada pelo Jurídico. Minutas Padrão,
        // Cláusulas Padrão e Modelos de NDA e Procuração usam o mesmo cadastro e aprovação.
        key: 'navGroups.legalLibrary',
        items: [
          {
            to: '/legal/library/templates',
            key: 'nav.legalTemplates',
            icon: Library,
            permission: ['legal.template.read', 'legal.template.manage', 'legal.template.approve'],
          },
          {
            to: '/legal/library/clauses',
            key: 'nav.legalClauses',
            icon: ScrollText,
            permission: ['legal.template.read', 'legal.template.manage', 'legal.template.approve'],
          },
          {
            to: '/legal/library/models',
            key: 'nav.legalModels',
            icon: FileSignature,
            permission: ['legal.template.read', 'legal.template.manage', 'legal.template.approve'],
          },
        ],
      },
      {
        // Contencioso (ADR 0064): processos, com o escritório pela demanda externa.
        key: 'navGroups.legalLitigation',
        items: [
          {
            to: '/legal/lawsuits',
            key: 'nav.legalLawsuits',
            icon: Scale,
            permission: 'legal.request.read',
          },
        ],
      },
      {
        key: 'navGroups.legalManagement',
        items: [
          {
            to: '/legal/agenda',
            key: 'nav.legalAgenda',
            icon: CalendarClock,
            permission: 'legal.request.read',
          },
          {
            to: '/legal/alerts',
            key: 'nav.legalAlerts',
            icon: BellRing,
            permission: 'legal.request.read',
          },
          {
            to: '/legal/reports',
            key: 'nav.legalReports',
            icon: ChartColumn,
            permission: 'legal.request.read',
          },
          {
            to: '/legal/external',
            key: 'nav.legalExternal',
            icon: Briefcase,
            permission: 'legal.request.read',
          },
          {
            to: '/legal/catalog',
            key: 'nav.legalCatalog',
            icon: FileCog,
            permission: 'legal.catalog.manage',
          },
        ],
      },
    ],
  },
  {
    // Recursos Humanos: por enquanto só a Comunicação interna, que publica o conteúdo da intranet
    // no Início (ADR 0077). Os demais módulos do RH entram aqui quando forem construídos.
    id: 'hr',
    key: 'areas.hr',
    railKey: 'areasShort.hr',
    icon: HeartHandshake,
    groups: [
      { key: null, items: [overview('/hr')] },
      {
        key: 'navGroups.internalCommunication',
        items: [
          {
            to: '/hr/intranet',
            key: 'nav.intranetPosts',
            icon: Megaphone,
            permission: 'intranet.content.manage',
          },
          {
            to: '/hr/intranet/links',
            key: 'nav.intranetLinks',
            icon: Link2,
            permission: 'intranet.content.manage',
          },
        ],
      },
    ],
  },
  preparing('personnel', IdCard, 'areasShort.personnel'),
  {
    // Tecnologia da Informação (ADR 0034), Fase 1: painel, contratações e assinaturas de software
    // e SaaS (com lotes de licenças e atribuições) e o catálogo de softwares. Fase 2 (ADR 0037):
    // equipamentos (visão técnica sobre o patrimônio), kit por pessoa, devoluções e importação.
    id: 'it',
    key: 'areas.it',
    railKey: 'areasShort.it',
    icon: MonitorCog,
    groups: [
      {
        key: null,
        items: [
          overview('/it'),
          // Os cartões da Visão geral, mesmo os zerados (ADR 0097).
          {
            to: '/it/pending',
            key: 'nav.itPending',
            icon: ListChecks,
            permission: [
              'it.ticket.work',
              'it.ticket.security',
              ...IT_DEVICE_READ,
              ...IT_SUBSCRIPTION_READ,
            ],
          },
        ],
      },
      {
        // Service Desk (ADR 0082): abrir e acompanhar os próprios chamados só com o login; a Central
        // de atendimento para quem atende as filas (a sigilosa só com it.ticket.security).
        key: 'navGroups.itServiceDesk',
        items: [
          {
            to: '/it/tickets/new',
            key: 'nav.itTicketNew',
            icon: MessageSquarePlus,
            permission: null,
          },
          { to: '/it/tickets/mine', key: 'nav.itMyTickets', icon: Inbox, permission: null },
          {
            to: '/it/service-desk',
            key: 'nav.itServiceDesk',
            icon: Headset,
            permission: ['it.ticket.work', 'it.ticket.security'],
          },
          // SD-4: base de conhecimento para todos; problemas e painel para quem atende.
          { to: '/it/knowledge', key: 'nav.itKnowledge', icon: Library, permission: null },
          { to: '/it/problems', key: 'nav.itProblems', icon: Link2, permission: 'it.ticket.work' },
          // SD-5: rotinas periódicas e preventiva básica (nada roda sozinho).
          {
            to: '/it/routines',
            key: 'nav.itRoutines',
            icon: History,
            permission: 'it.ticket.work',
          },
          {
            to: '/it/service-desk/dashboard',
            key: 'nav.itDeskDashboard',
            icon: Gauge,
            permission: ['it.ticket.work', 'it.ticket.security'],
          },
        ],
      },
      {
        key: 'navGroups.itSoftware',
        items: [
          // Catálogo homologado e as próprias licenças: todos com login (auditoria de 27/10).
          { to: '/it/my-software', key: 'nav.itMySoftware', icon: Package, permission: null },
          // Aceite das políticas de uso e revisão dos acessos da equipe: todos com login (0341).
          { to: '/it/my-access', key: 'nav.itMyAccess', icon: ClipboardCheck, permission: null },
          {
            to: '/it/dashboard',
            key: 'nav.itDashboard',
            icon: Gauge,
            permission: IT_SUBSCRIPTION_READ,
          },
          {
            to: '/it/subscriptions',
            key: 'nav.itSubscriptions',
            icon: ReceiptText,
            permission: IT_SUBSCRIPTION_READ,
          },
          {
            to: '/it/subscriptions/new',
            key: 'nav.itNew',
            icon: CirclePlus,
            permission: 'it.subscription.manage',
          },
          {
            // Importação de contratações, lotes e atribuições por planilha (B0.6), sensível.
            to: '/it/subscriptions/import',
            key: 'nav.itLicenseImport',
            icon: FileUp,
            permission: 'it.license.import',
          },
        ],
      },
      {
        key: 'navGroups.itAccess',
        items: [
          { to: '/it/access', key: 'nav.itAccess', icon: KeyRound, permission: IT_ACCESS_READ },
          { to: '/it/systems', key: 'nav.itSystems', icon: Server, permission: IT_ACCESS_READ },
          {
            to: '/it/access/dashboard',
            key: 'nav.itAccessDashboard',
            icon: Gauge,
            permission: IT_ACCESS_READ,
          },
          {
            to: '/it/access-reviews',
            key: 'nav.itAccessReviews',
            icon: ListChecks,
            permission: IT_ACCESS_READ,
          },
          {
            to: '/it/policies',
            key: 'nav.itPolicies',
            icon: ScrollText,
            permission: IT_ACCESS_READ,
          },
          {
            to: '/it/software-reviews',
            key: 'nav.itSoftwareReviews',
            icon: CalendarCheck,
            permission: ['it.catalog.read', 'it.catalog.manage'],
          },
        ],
      },
      {
        key: 'navGroups.itGovernance',
        items: [
          {
            to: '/it/governance',
            key: 'nav.itGovernanceDashboard',
            icon: Gauge,
            permission: IT_GOVERNANCE_READ,
          },
          {
            to: '/it/controls',
            key: 'nav.itControls',
            icon: ListChecks,
            permission: IT_GOVERNANCE_READ,
          },
          {
            to: '/it/control-exceptions',
            key: 'nav.itControlExceptions',
            icon: ShieldAlert,
            permission: IT_GOVERNANCE_READ,
          },
          { to: '/it/raci', key: 'nav.itRaci', icon: Users, permission: IT_GOVERNANCE_READ },
          {
            to: '/it/incidents',
            key: 'nav.itIncidents',
            icon: Siren,
            permission: ['it.incident.manage', 'it.ticket.security'],
          },
          {
            to: '/it/emergency-vault',
            key: 'nav.itEmergencyVault',
            icon: Vault,
            permission: IT_GOVERNANCE_READ,
          },
        ],
      },
      {
        key: 'navGroups.itProjects',
        items: [
          {
            to: '/it/projects',
            key: 'nav.itProjects',
            icon: FolderKanban,
            permission: IT_PROJECT_READ,
          },
          {
            to: '/it/changes',
            key: 'nav.itChanges',
            icon: GitPullRequestArrow,
            permission: IT_PROJECT_READ,
          },
        ],
      },
      {
        key: 'navGroups.itFinance',
        items: [
          { to: '/it/services', key: 'nav.itServices', icon: Network, permission: IT_FINANCE_READ },
          { to: '/it/invoices', key: 'nav.itInvoices', icon: Receipt, permission: IT_FINANCE_READ },
          { to: '/it/costs', key: 'nav.itCosts', icon: Calculator, permission: IT_FINANCE_READ },
          {
            to: '/it/finance',
            key: 'nav.itFinanceDashboard',
            icon: ChartColumn,
            permission: IT_FINANCE_READ,
          },
        ],
      },
      {
        key: 'navGroups.itDevices',
        items: [
          {
            to: '/it/devices',
            key: 'nav.itDevices',
            icon: Laptop,
            permission: IT_DEVICE_READ,
          },
          // Estoque de peças e preventiva (M1 a M3, 0343).
          { to: '/it/stock', key: 'nav.itStock', icon: Boxes, permission: IT_DEVICE_READ },
          {
            to: '/it/preventive',
            key: 'nav.itPreventive',
            icon: CalendarClock,
            permission: IT_DEVICE_READ,
          },
          {
            to: '/it/people',
            key: 'nav.itPeople',
            icon: UserCheck,
            permission: IT_DEVICE_READ,
          },
          {
            to: '/it/onboardings',
            key: 'nav.itOnboardings',
            icon: UserPlus,
            permission: IT_DEVICE_READ,
          },
          {
            to: '/it/offboardings',
            key: 'nav.itOffboardings',
            icon: ClipboardCheck,
            permission: IT_DEVICE_READ,
          },
          {
            to: '/it/devices/dashboard',
            key: 'nav.itDevicesDashboard',
            icon: ChartColumn,
            permission: IT_DEVICE_READ,
          },
          {
            to: '/it/devices/import',
            key: 'nav.itDeviceImport',
            icon: FileUp,
            permission: 'it.device.import',
          },
          {
            // Fila do Patrimônio para o envio e o retorno dos reparos da TI (M1), sem o módulo de TI.
            to: '/it/repair-movements',
            key: 'nav.itRepairMovements',
            icon: Wrench,
            permission: 'asset.it.manage',
          },
        ],
      },
      {
        key: 'navGroups.itManagement',
        items: [
          {
            to: '/it/catalog',
            key: 'nav.itCatalog',
            icon: AppWindow,
            permission: ['it.catalog.read', 'it.catalog.manage', 'it.subscription.read'],
          },
          {
            to: '/it/service-desk/catalog',
            key: 'nav.itTicketCatalog',
            icon: ListChecks,
            permission: 'it.ticket.manage',
          },
        ],
      },
    ],
  },
  {
    id: 'fpa',
    key: 'areas.fpa',
    icon: TrendingUp,
    // Fluxo operacional do FP&A (ADR 0049, PR-8): a página inicial é o "Meu planejamento" (versão
    // aberta e os botões Nova receita, Novo custo e Nova despesa); em seguida a análise dos
    // resultados; a administração do planejamento fica por último e só para quem a conduz. Os
    // cadastros corporativos continuam só em Dados Mestres; o Plano de contas gerencial fica aqui
    // só para consulta. As rotas antigas (/fpa/budget, /fpa/forecast) abrem Planejamento.
    groups: [
      {
        key: null,
        items: [
          // A página inicial do FP&A é o Meu planejamento (PR-8).
          overview('/fpa', 'nav.fpaHome'),
          { to: '/fpa/planning', key: 'nav.fpaPlanning', icon: PiggyBank, permission: FPA_PLAN },
          // Todos os cartões da Visão geral, mesmo os zerados (ADR 0097).
          {
            to: '/fpa/pending',
            key: 'nav.fpaPending',
            icon: ListChecks,
            permission: 'fpa.data.read',
          },
        ],
      },
      {
        key: 'navGroups.fpaAnalysis',
        items: [
          // DRE gerencial (ADR 0049, PR-6 e PR-7): a estrutura do mapeamento vigente, com comparação.
          {
            to: '/fpa/pl-report',
            key: 'nav.fpaPlReport',
            icon: ListTree,
            permission: 'fpa.data.read',
          },
          {
            to: '/fpa/compare',
            key: 'nav.fpaCompare',
            icon: ChartColumn,
            permission: 'fpa.data.read',
          },
          // Gastos de aquisição (ADR 0032, 13): lançamentos marcados, por período e empresa.
          {
            to: '/fpa/acquisition',
            key: 'nav.fpaAcquisition',
            icon: ChartColumn,
            permission: 'fpa.data.read',
          },
          // Painel executivo da DRE (ADR 0049, PR-7d): resultados, margens e evolução.
          {
            to: '/fpa/pl-dashboard',
            key: 'nav.fpaPlDashboard',
            icon: ChartLine,
            permission: 'fpa.data.read',
          },
          {
            to: '/fpa/dashboard',
            key: 'nav.fpaDashboard',
            icon: Gauge,
            permission: 'fpa.data.read',
          },
          {
            to: '/fpa/actuals',
            key: 'nav.fpaActuals',
            icon: Receipt,
            permission: ['fpa.data.read', 'fpa.actual.manage'],
          },
        ],
      },
      // Administração do FP&A: só para quem conduz o planejamento (a tela continua conferindo a
      // permissão de cada ação, e o servidor também).
      {
        key: 'navGroups.fpaAdmin',
        items: [
          {
            to: '/fpa/settings/calendar',
            key: 'nav.fpaCalendar',
            icon: CalendarDays,
            permission: FPA_ADMIN_CYCLE,
          },
          {
            to: '/fpa/scenarios',
            key: 'nav.fpaScenarios',
            icon: GitBranch,
            permission: FPA_ADMIN,
          },
          {
            to: '/fpa/settings/assumptions',
            key: 'nav.fpaAssumptions',
            icon: Variable,
            permission: FPA_ADMIN,
          },
          {
            to: '/fpa/reference',
            key: 'nav.fpaReference',
            icon: History,
            permission: FPA_ADMIN,
          },
          {
            to: '/fpa/settings/catalog',
            key: 'nav.fpaCatalog',
            icon: Library,
            permission: FPA_ADMIN,
          },
          {
            to: '/fpa/settings/pl',
            key: 'nav.fpaPlLines',
            icon: ListTree,
            permission: [...FPA_ADMIN, 'fpa.actual.manage'],
          },
          {
            to: '/fpa/settings/accounts',
            key: 'nav.fpaManagementAccounts',
            icon: ListTree,
            permission: FPA_ADMIN,
          },
          // Fila de exceções de classificação (ADR 0049, PR-3c): quem conduz versões decide.
          {
            to: '/fpa/exceptions',
            key: 'nav.fpaExceptions',
            icon: ShieldCheck,
            permission: 'fpa.plan.manage',
          },
          {
            to: '/fpa/settings/access',
            key: 'nav.fpaAccess',
            icon: KeyRound,
            permission: 'fpa.access.manage',
          },
          // Lista de pessoal (ADR 0049, R14): só quem planeja pessoal.
          {
            to: '/fpa/settings/payroll',
            key: 'nav.fpaPayroll',
            icon: ShieldCheck,
            permission: 'fpa.payroll.manage',
          },
        ],
      },
    ],
  },
  preparing('administrative', Briefcase, 'areasShort.administrative'),
  {
    // Ajuda: a Central de Conhecimento (ADR 0018), só para colaboradores internos com acesso.
    id: 'help',
    key: 'areas.help',
    icon: LifeBuoy,
    bottom: true,
    groups: [
      {
        key: 'navGroups.knowledgeCenter',
        items: [
          {
            to: '/knowledge',
            key: 'nav.knowledgeLibrary',
            icon: Library,
            permission: KNOWLEDGE_READ,
          },
          {
            to: '/knowledge/manuals',
            key: 'nav.knowledgeManuals',
            icon: BookOpen,
            permission: KNOWLEDGE_READ,
          },
          {
            to: '/knowledge/pops',
            key: 'nav.knowledgePops',
            icon: ClipboardList,
            permission: KNOWLEDGE_READ,
          },
          {
            to: '/knowledge/itos',
            key: 'nav.knowledgeItos',
            icon: FileCheck,
            permission: KNOWLEDGE_READ,
          },
          {
            to: '/knowledge/news',
            key: 'nav.knowledgeNews',
            icon: Sparkles,
            permission: KNOWLEDGE_READ,
          },
          {
            to: '/knowledge/faq',
            key: 'nav.knowledgeFaq',
            icon: CircleHelp,
            permission: KNOWLEDGE_READ,
          },
          {
            to: '/knowledge/review',
            key: 'nav.knowledgeReview',
            icon: ClipboardCheck,
            permission: KNOWLEDGE_READ.filter((p) => p !== 'knowledge.document.read'),
          },
          {
            to: '/knowledge/issues',
            key: 'nav.knowledgeIssues',
            icon: TriangleAlert,
            permission: 'knowledge.library.manage',
          },
          { to: '/help', key: 'nav.help', icon: LifeBuoy, permission: 'help.article.manage' },
        ],
      },
    ],
  },
  {
    // Dados Mestres (ADR 0039): os cadastros corporativos num só lugar. Desde 19/10/2026 o menu é
    // enxuto: a visão geral e oito seções, cada uma com a sua página de telas. Governança e Alçadas
    // e a Plataforma (Segurança e Acessos, Auditoria e Suporte) voltaram para cá; a Administração
    // da Plataforma deixou de ser uma entrada própria na barra (/platform leva a /system/platform).
    // As telas e os endereços são os mesmos de antes; Subsidiárias é aberta pela tela Empresas.
    id: 'system',
    key: 'areas.system',
    railKey: 'areasShort.system',
    icon: SlidersHorizontal,
    bottom: true,
    sections: [
      { id: 'corporate', key: 'navGroups.corporateStructure', icon: Building2 },
      { id: 'people', key: 'navGroups.peopleOrganization', icon: Users },
      { id: 'careers', key: 'navGroups.jobsCareers', icon: Briefcase },
      { id: 'finance', key: 'navGroups.financialClassifications', icon: Landmark },
      { id: 'partners', key: 'navGroups.commercial', icon: Handshake },
      { id: 'imports', key: 'navGroups.dataImport', icon: Upload },
      { id: 'governance', key: 'navGroups.governance', icon: Scale },
      { id: 'platform', key: 'navSections.platform', icon: ShieldCheck },
    ],
    groups: [
      {
        key: null,
        items: [
          overview('/system'),
          {
            to: '/masterdata/pending',
            key: 'nav.masterdataPending',
            icon: ListChecks,
            permission: MASTERDATA_PENDING,
          },
        ],
      },
      {
        key: 'navGroups.corporateStructure',
        section: 'corporate',
        items: [
          {
            to: '/legal-entities',
            key: 'nav.establishments',
            icon: Building2,
            permission: 'core.legal_entity.read',
          },
          { to: '/branches', key: 'nav.branches', icon: Store, permission: 'core.branch.read' },
          {
            to: '/facilities/locations',
            key: 'nav.facilitiesLocations',
            icon: MapPin,
            permission: 'core.location.manage',
          },
          {
            to: '/structure/business-units',
            key: 'nav.businessUnits',
            icon: Building2,
            permission: 'structure.catalog.read',
          },
          {
            to: '/departments',
            key: 'nav.departments',
            icon: Network,
            permission: ['core.department.manage', 'structure.catalog.read'],
          },
          {
            to: '/governance/cost-centers',
            key: 'nav.costCenters',
            icon: Landmark,
            permission: [
              'core.cost_center.read',
              'core.cost_center.manage',
              'structure.catalog.read',
            ],
          },
          {
            to: '/representatives',
            key: 'nav.representatives',
            icon: Users,
            permission: [
              'core.representative.read',
              'core.representative.manage',
              'core.representative.validate',
            ],
          },
        ],
      },
      {
        key: 'navGroups.peopleOrganization',
        section: 'people',
        items: [
          { to: '/people/persons', key: 'nav.persons', icon: Users, permission: PERSON_READ },
          { to: '/people/employees', key: 'nav.employees', icon: IdCard, permission: PEOPLE_READ },
          {
            to: '/people/employment-types',
            key: 'nav.employmentTypes',
            icon: Tags,
            permission: PEOPLE_READ,
          },
          {
            to: '/people/movements',
            key: 'nav.movements',
            icon: History,
            permission: PEOPLE_ADMIN,
          },
          {
            to: '/people/allocations',
            key: 'nav.allocations',
            icon: Coins,
            permission: PEOPLE_ADMIN,
          },
          { to: '/people/org-chart', key: 'nav.orgChart', icon: Network, permission: PEOPLE_READ },
          {
            to: '/people/birthdays',
            key: 'nav.birthdays',
            icon: Cake,
            permission: [...PEOPLE_READ, 'people.private.read', 'people.private.manage'],
          },
          {
            to: '/people/mailing',
            key: 'nav.mailing',
            icon: Gift,
            permission: ['people.mailing.read'],
          },
          {
            to: '/people/preferences',
            key: 'nav.preferences',
            icon: Shirt,
            permission: ['people.preferences.manage'],
          },
          {
            to: '/people/corrections',
            key: 'nav.corrections',
            icon: MessageSquareWarning,
            permission: ['people.private.manage', 'people.hr_request.work'],
          },
          {
            to: '/people/access',
            key: 'nav.accessGrant',
            icon: KeyRound,
            permission: ['people.access.grant'],
          },
          {
            to: '/people/payroll-cost',
            key: 'nav.payrollCost',
            icon: Receipt,
            permission: ['people.payroll_cost.manage', 'fpa.payroll.read'],
          },
          {
            to: '/people/acknowledgements',
            key: 'nav.acknowledgements',
            icon: ClipboardCheck,
            permission: ['people.acknowledgement.read'],
          },
        ],
      },
      {
        key: 'navGroups.jobsCareers',
        section: 'careers',
        items: [
          {
            to: '/career/families',
            key: 'nav.jobFamilies',
            icon: ListTree,
            permission: CAREER_READ,
          },
          { to: '/career/levels', key: 'nav.jobLevels', icon: TrendingUp, permission: CAREER_READ },
          {
            to: '/career/tracks',
            key: 'nav.careerTracks',
            icon: GitBranch,
            permission: CAREER_READ,
          },
          { to: '/career/grades', key: 'nav.salaryGrades', icon: Award, permission: CAREER_READ },
          { to: '/career/jobs', key: 'nav.jobs', icon: Briefcase, permission: CAREER_READ },
        ],
      },
      {
        key: 'navGroups.financialClassifications',
        section: 'finance',
        items: [
          {
            to: '/masterdata/accounting-accounts',
            key: 'nav.accountingAccounts',
            icon: ListTree,
            permission: 'structure.catalog.read',
          },
          {
            to: '/structure/accounts',
            key: 'nav.managementAccounts',
            icon: ListTree,
            permission: 'structure.catalog.read',
          },
          {
            to: '/structure/classifications/CATEGORIA-FINANCEIRA',
            key: 'nav.financialCategories',
            icon: Tags,
            permission: 'structure.catalog.read',
          },
          {
            to: '/structure/classifications/TIPO-CLASSIFICACAO',
            key: 'nav.classificationTypes',
            icon: ClipboardList,
            permission: 'structure.catalog.read',
          },
          {
            to: '/structure/classifications/TIPO-GASTO',
            key: 'nav.expenseTypes',
            icon: Coins,
            permission: 'structure.catalog.read',
          },
          {
            to: '/currencies',
            key: 'nav.currencies',
            icon: Coins,
            permission: 'core.currency.read',
          },
          // Cadastro único das contas das empresas (o mesmo da Tesouraria, ADR 0073).
          {
            to: '/masterdata/bank-accounts',
            key: 'nav.companyBankAccounts',
            icon: Building2,
            permission: 'finance.bank_account.read',
          },
          {
            to: '/structure/categories',
            key: 'nav.classificationCategories',
            icon: LayoutGrid,
            permission: 'structure.catalog.admin',
          },
        ],
      },
      {
        key: 'navGroups.commercial',
        section: 'partners',
        items: [
          {
            to: '/partners',
            key: 'nav.partners',
            icon: Handshake,
            permission: PARTNER_READ,
          },
          {
            to: '/partners/portal-access',
            key: 'nav.partnerPortalAccess',
            icon: KeyRound,
            permission: ['partner.portal.read', 'partner.portal.invite', 'partner.portal.revoke'],
          },
          {
            to: '/partners/document-types',
            key: 'nav.partnerDocumentTypes',
            icon: FileCog,
            permission: 'partner.document_type.manage',
          },
        ],
      },
      {
        key: 'navGroups.dataImport',
        section: 'imports',
        items: [
          {
            to: '/masterdata/imports',
            key: 'nav.imports',
            icon: Upload,
            permission: 'masterdata.import.read',
          },
          {
            to: '/masterdata/imports/history',
            key: 'nav.importHistory',
            icon: History,
            permission: 'masterdata.import.read',
          },
          {
            to: '/structure/import',
            key: 'nav.structureImport',
            icon: Upload,
            permission: 'structure.catalog.admin',
          },
          {
            to: '/structure/import-pending',
            key: 'nav.importPending',
            icon: ClipboardList,
            permission: 'structure.catalog.read',
          },
        ],
      },
      // Governança e Alçadas e a Plataforma: governança das aprovações, segurança e acessos (P-01),
      // auditoria (P-02) e os reportes de bugs, com as permissões e os endereços de sempre.
      {
        key: 'navGroups.governance',
        section: 'governance',
        items: [
          {
            to: '/governance/matrix',
            key: 'nav.approvalMatrix',
            icon: Scale,
            permission: [
              'approval.governance.manage',
              'approval.request.decide',
              'core.cost_center.read',
            ],
          },
          {
            to: '/governance/absences',
            key: 'nav.approvalAbsences',
            icon: CalendarOff,
            permission: ['approval.governance.manage', 'approval.request.decide', APPROVER_NOMINEE],
          },
          // Prazos de atendimento (SLA, ADR 0061): catálogo, calendários e políticas.
          {
            to: '/governance/sla/processes',
            key: 'nav.slaProcesses',
            icon: ListTree,
            permission: ['core.process.manage', 'core.calendar.manage', 'sla.policy.propose'],
          },
          {
            to: '/governance/sla/calendars',
            key: 'nav.slaCalendars',
            icon: CalendarRange,
            permission: ['core.process.manage', 'core.calendar.manage', 'sla.policy.propose'],
          },
          {
            to: '/governance/sla/dashboard',
            key: 'nav.slaDashboard',
            icon: Gauge,
            permission: [
              'sla.report.read',
              'sla.audit.read',
              'sla.policy.propose',
              'core.process.manage',
            ],
          },
          {
            to: '/governance/sla/policies',
            key: 'nav.slaPolicies',
            icon: Timer,
            permission: ['core.process.manage', 'core.calendar.manage', 'sla.policy.propose'],
          },
          // Política Corporativa de Viagens (F02): valores e regras por versão, mantidos por Facilities.
          {
            to: '/facilities/travel-policy',
            key: 'nav.travelPolicy',
            icon: Plane,
            permission: [
              'facilities.travel_policy.read',
              'facilities.travel_policy.manage',
              'facilities.travel_policy.approve',
            ],
          },
        ],
      },
      {
        key: 'navGroups.access',
        section: 'platform',
        items: [
          { to: '/users', key: 'nav.users', icon: Users, permission: 'iam.user.read' },
          { to: '/roles', key: 'nav.roles', icon: ShieldCheck, permission: 'iam.role.read' },
          {
            to: '/access-requests',
            key: 'nav.accessRequests',
            icon: ClipboardCheck,
            permission: 'iam.access.grant',
          },
          {
            to: '/single-holder',
            key: 'nav.singleHolder',
            icon: KeyRound,
            permission: 'iam.user.read',
          },
        ],
      },
      {
        key: 'navGroups.platformControl',
        section: 'platform',
        items: [
          {
            to: '/audit',
            key: 'nav.audit',
            icon: History,
            permission: ['audit.log.read', 'audit.access.read'],
          },
          { to: '/feedback', key: 'nav.feedback', icon: Bug, permission: 'feedback.report.manage' },
        ],
      },
    ],
  },
];

export function itemsOf(area: NavArea): NavItem[] {
  return area.groups.flatMap((g) => g.items);
}

/** As telas de verdade do departamento, sem a página inicial. */
export function screensOf(area: NavArea): NavItem[] {
  return itemsOf(area).filter((i) => !i.overview);
}

/** Endereço da página de uma seção: `/<departamento>/<seção>`. */
export function sectionPath(area: NavArea, section: NavSection): string {
  return `/${area.id}/${section.id}`;
}

/** As seções que aparecem: só as que têm algum grupo (com `visibleAreas`, alguma tela liberada). */
export function sectionsOf(area: NavArea): NavSection[] {
  return (area.sections ?? []).filter((s) => area.groups.some((g) => g.section === s.id));
}

/** Os grupos de telas de uma seção. */
export function groupsOf(area: NavArea, section: NavSection): NavGroup[] {
  return area.groups.filter((g) => g.section === section.id);
}

/** O departamento e a seção cuja página é este endereço. */
export function sectionAt(
  areas: NavArea[],
  pathname: string,
): { area: NavArea; section: NavSection } | undefined {
  for (const area of areas)
    for (const section of area.sections ?? [])
      if (sectionPath(area, section) === pathname) return { area, section };
  return undefined;
}

/**
 * A seção que fica marcada no menu enxuto: a da própria página da seção ou a da tela aberta.
 * Na visão geral (e fora do departamento), nenhuma.
 */
export function activeSectionId(area: NavArea, pathname: string): string | undefined {
  const page = sectionAt([area], pathname);
  if (page) return page.section.id;
  const { group, item } = locate([area], pathname);
  return item && !item.overview ? group?.section : undefined;
}

/**
 * Os departamentos com só as telas que a pessoa pode ver. Departamento com telas, mas
 * nenhuma liberada, some. Departamento ainda em preparação (sem telas) aparece para todos, e o
 * que tem só a página inicial segue a permissão dela (Dashboard executivo).
 */
export function visibleAreas(allowed: (permission: string) => boolean): NavArea[] {
  return AREAS.filter((area) => {
    const screens = screensOf(area);
    return screens.length === 0
      ? itemsOf(area).every((i) => permitted(i, allowed))
      : screens.some((i) => permitted(i, allowed));
  }).map((area) => ({
    ...area,
    groups: area.groups
      .map((g) => ({
        ...g,
        items: g.items.filter((i) => permitted(i, allowed)),
      }))
      .filter((g) => g.items.length > 0),
  }));
}

function permitted(item: NavItem, allowed: (permission: string) => boolean): boolean {
  if (item.permission === null) return true;
  const list = typeof item.permission === 'string' ? [item.permission] : item.permission;
  return list.some(allowed);
}

function matches(item: NavItem, pathname: string): boolean {
  return item.to === '/'
    ? pathname === '/'
    : pathname === item.to || pathname.startsWith(`${item.to}/`);
}

/**
 * Telas internas: não ficam no menu porque são abertas de dentro de outra tela (o menu mostra os
 * processos, não ações repetidas). O caminho mostra a tela de origem e a própria tela, e o
 * departamento certo fica marcado. Ex.: Facilities › Patrimônio › Bens › Novo bem.
 */
export interface InnerScreen {
  match: (pathname: string) => boolean;
  /** Endereço do item do menu de onde a tela é aberta. */
  parent: string;
  /** Nome da tela no caminho (chave de tradução). */
  key: string;
}
export const INNER_SCREENS: readonly InnerScreen[] = [
  // Subsidiárias (código do NetSuite e eliminação) é aberta pela tela Empresas, até ir para a
  // ficha da Empresa (Integração e consolidação).
  {
    match: (p) => p === '/structure/subsidiaries',
    parent: '/legal-entities',
    key: 'nav.subsidiaries',
  },
  { match: (p) => p === '/assets/new', parent: '/assets/list', key: 'assets.new.title' },
  { match: (p) => p === '/assets/labels', parent: '/assets/list', key: 'assets.labels.title' },
  {
    match: (p) => /^\/assets\/[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(p),
    parent: '/assets/list',
    key: 'assets.detail.crumb',
  },
  // FP&A: Budget e Forecast viraram abas de Planejamento; os endereços antigos e a ficha da versão
  // continuam abrindo e marcam Planejamento no menu.
  { match: (p) => p === '/fpa/budget', parent: '/fpa/planning', key: 'nav.fpaBudget' },
  { match: (p) => p === '/fpa/forecast', parent: '/fpa/planning', key: 'nav.fpaForecast' },
  {
    match: (p) => /^\/fpa\/versions\/[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(p),
    parent: '/fpa/planning',
    key: 'fpaPlan.versions.crumb',
  },
  // Ficha do chamado de TI: aberta por Meus chamados ou pela Central de atendimento.
  {
    match: (p) => /^\/it\/tickets\/[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(p),
    parent: '/it/tickets/mine',
    key: 'it.desk.detail.crumb',
  },
  // Artigo da base de conhecimento e ficha do problema (SD-4).
  {
    match: (p) => /^\/it\/knowledge\/[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(p),
    parent: '/it/knowledge',
    key: 'it.kb.crumb',
  },
  {
    match: (p) => /^\/it\/problems\/[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(p),
    parent: '/it/problems',
    key: 'it.problems.crumb',
  },
  {
    match: (p) => /^\/it\/routines\/[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(p),
    parent: '/it/routines',
    key: 'it.routines.crumb',
  },
];

/** A tela interna do endereço, com o item de origem que a pessoa vê no menu. */
function innerScreen(areas: NavArea[], pathname: string) {
  const inner = INNER_SCREENS.find((s) => s.match(pathname));
  if (!inner) return undefined;
  for (const area of areas)
    for (const group of area.groups)
      for (const item of group.items)
        if (item.to === inner.parent) return { area, group, item, inner };
  return undefined;
}

/** O departamento, o módulo e a tela do endereço atual (a tela de endereço mais longo vence). */
export function locate(
  areas: NavArea[],
  pathname: string,
): {
  area: NavArea | undefined;
  group: NavGroup | undefined;
  item: NavItem | undefined;
  inner?: InnerScreen;
} {
  const inside = innerScreen(areas, pathname);
  if (inside) return inside;
  let found: { area: NavArea; group: NavGroup; item: NavItem } | undefined;
  for (const area of areas)
    for (const group of area.groups)
      for (const item of group.items)
        if (matches(item, pathname) && (!found || item.to.length > found.item.to.length))
          found = { area, group, item };
  return found ?? { area: areas[0], group: undefined, item: undefined };
}

/**
 * O item do menu que fica marcado no endereço atual: só um, o de endereço mais longo (a mesma regra
 * do caminho). Assim "Painel de ativos" (/it/devices/dashboard) não acende também "Equipamentos de
 * TI" (/it/devices), e a ficha de um equipamento continua marcando "Equipamentos de TI".
 */
export function activeItemTo(areas: NavArea[], pathname: string): string | undefined {
  const item = locate(areas, pathname).item;
  // A visão geral (e o Início) só fica marcada na própria página, como sempre foi.
  if (!item || ((item.overview === true || item.to === '/') && item.to !== pathname))
    return undefined;
  return item.to;
}

/**
 * O que a lateral única abre sozinha no endereço atual (ADR 0071): o departamento da tela e, nos
 * departamentos com módulos, o módulo dela. No Início (e nos itens soltos do topo), nenhum.
 */
export function menuTarget(
  areas: NavArea[],
  pathname: string,
): { areaId: string | undefined; groupKey: string | undefined } {
  const { area, group, item } = locate(areas, pathname);
  if (!area || !item || area.id === 'home') return { areaId: undefined, groupKey: undefined };
  // Nas seções (Dados Mestres) o menu não abre módulos: a seção marcada basta.
  return { areaId: area.id, groupKey: area.sections ? undefined : (group?.key ?? undefined) };
}

/** A tela do menu com este endereço, se a pessoa pode vê-la (atalhos do Início usam a mesma regra). */
export function visibleItem(areas: NavArea[], to: string): NavItem | undefined {
  for (const area of areas) for (const item of itemsOf(area)) if (item.to === to) return item;
  return undefined;
}

export interface Crumb {
  key: string;
  /** Endereço para voltar; sem endereço = onde a pessoa está (ou um módulo, que não é tela). */
  to?: string;
}

/**
 * Caminho "Início › Departamento › Módulo › Tela" do endereço atual. Nos departamentos com seções:
 * "Início › Departamento › Seção › Grupo (quando a seção tem mais de um) › Tela", com a seção
 * levando à página dela.
 */
export function breadcrumbs(areas: NavArea[], pathname: string): Crumb[] {
  const { area, group, item, inner } = locate(areas, pathname);
  if (!area || !item || item.to === '/') return [];
  // A primeira tela é a porta de entrada do departamento (a página inicial, ou a Ajuda).
  const home = itemsOf(area)[0]!;
  const crumbs: Crumb[] = [
    { key: 'nav.home', to: '/' },
    home.to === pathname ? { key: area.key } : { key: area.key, to: home.to },
  ];
  if (item === home) {
    // A página de uma seção fica "dentro" da visão geral pelo endereço (/system/platform).
    const page = sectionAt([area], pathname);
    if (page) crumbs.push({ key: page.section.key });
    return crumbs;
  }
  const section = area.sections?.find((s) => s.id === group?.section);
  if (section) crumbs.push({ key: section.key, to: sectionPath(area, section) });
  if (group?.key && group.key !== section?.key) crumbs.push({ key: group.key });
  crumbs.push(item.to === pathname ? { key: item.key } : { key: item.key, to: item.to });
  if (inner) crumbs.push({ key: inner.key });
  return crumbs;
}
